// CS-2018 - EC2 server-control dashboard (Super Admin)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import {
  Ec2ActionRequest,
  getEc2AuditLogsForInstance,
  getEc2Instances,
  startEc2Instance,
  stopEc2Instance,
} from "../../../service/Ec2Service";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import { useAppSelector } from "../redux-hooks";

// Mirrors the backend Ec2InstanceDTO — guard flags (canStop/stopBlockedReason)
// are resolved server-side; the client only renders them.
export type Ec2Instance = {
  instanceId: string;
  name: string;
  type: string;
  elasticIp: string;
  state: string;
  status: string;
  uptime: string;
  launchTime: string;
  availabilityZone: string;
  platform: string;
  environment: string;
  // Product brand the instance serves, e.g. V3 BIOMED / WOUNDWAVE / MPM / MEDAUDIT / DEVOPS
  brand: string;
  currentEnvironment: boolean;
  protectedEnvironment: boolean;
  canStart: boolean;
  canStop: boolean;
  stopBlockedReason?: string;
};

export type Ec2DashboardData = {
  currentEnvironment: string;
  protectedEnvironments: string[];
  instances: Ec2Instance[];
};

export const EC2_TRANSITIONAL_STATES = ["pending", "starting", "stopping", "shutting-down", "initializing"];

// One audit-history entry for an instance (mirrors backend Ec2AuditLogDTO).
export type Ec2AuditLog = {
  auditLogId: number;
  action: string;
  actionType: string;
  auditTimestamp: string | number;
  actionBy: string;
};

type Ec2State = {
  dashboardData: Ec2DashboardData | null;
  fetchStatus: LoadingType;
  // instanceId of the row whose start/stop call is in flight, for a per-row spinner
  actionInstanceId: string | null;
  actionStatus: LoadingType;
  lastRefreshedAt: string | null;
  errorMessage: string | null;
  auditLogs: Ec2AuditLog[];
  auditLogsFetchStatus: LoadingType;
};

const initialState: Ec2State = {
  dashboardData: null,
  fetchStatus: "idle",
  actionInstanceId: null,
  actionStatus: "idle",
  lastRefreshedAt: null,
  errorMessage: null,
  auditLogs: [],
  auditLogsFetchStatus: "idle",
};

// On a failed V3Response code the http interceptor throws the axios response
// object itself, so the backend's message lives at error.data (not error.response.data).
const failedResponseOf = (error: any) => error?.data || error?.response?.data;

export const fetchEc2Instances = createAsyncThunk(
  "fetchEc2Instances",
  async (options: { silent?: boolean } | undefined, { rejectWithValue }) => {
    try {
      const response = await getEc2Instances(options?.silent);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(failedResponseOf(error) || "Failed to fetch EC2 instances");
    }
  },
);

export const startEc2 = createAsyncThunk("startEc2", async (request: Ec2ActionRequest, { rejectWithValue }) => {
  try {
    const response = await startEc2Instance(request);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(failedResponseOf(error) || "Failed to start the instance");
  }
});

export const stopEc2 = createAsyncThunk("stopEc2", async (request: Ec2ActionRequest, { rejectWithValue }) => {
  try {
    const response = await stopEc2Instance(request);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(failedResponseOf(error) || "Failed to stop the instance");
  }
});

export const fetchEc2AuditLogs = createAsyncThunk(
  "fetchEc2AuditLogs",
  async (request: Ec2ActionRequest, { rejectWithValue }) => {
    try {
      const response = await getEc2AuditLogsForInstance(request);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(failedResponseOf(error) || "Failed to fetch the audit history");
    }
  },
);

const ec2Slice = createSlice({
  name: "ec2",
  initialState,
  reducers: {
    resetEc2State: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEc2Instances.pending, (state, action) => {
        // Silent background polls keep the current view untouched — no spinners.
        if (!action.meta.arg?.silent) {
          state.fetchStatus = "loading";
        }
        state.errorMessage = null;
      })
      .addCase(fetchEc2Instances.fulfilled, (state, action) => {
        // The backend answers HTTP 200 with a failed V3Response when the control
        // Lambda is unreachable — surface that as an error, keep the last good data.
        if (action.payload?.code === SUCCESS_CODE && action.payload?.data) {
          state.fetchStatus = "success";
          state.dashboardData = action.payload.data;
          state.lastRefreshedAt = new Date().toISOString();
        } else {
          state.fetchStatus = "error";
          state.errorMessage = action.payload?.message || "Failed to fetch EC2 instances";
        }
      })
      .addCase(fetchEc2Instances.rejected, (state, action) => {
        state.fetchStatus = "error";
        state.errorMessage = (action.payload as any)?.message || "Failed to fetch EC2 instances";
      })
      .addCase(startEc2.pending, (state, action) => {
        state.actionStatus = "loading";
        state.actionInstanceId = action.meta.arg.instanceId ?? null;
      })
      .addCase(startEc2.fulfilled, (state) => {
        state.actionStatus = "success";
        state.actionInstanceId = null;
      })
      .addCase(startEc2.rejected, (state, action) => {
        state.actionStatus = "error";
        state.actionInstanceId = null;
        state.errorMessage = (action.payload as any)?.message || "Failed to start the instance";
      })
      .addCase(stopEc2.pending, (state, action) => {
        state.actionStatus = "loading";
        state.actionInstanceId = action.meta.arg.instanceId ?? null;
      })
      .addCase(stopEc2.fulfilled, (state) => {
        state.actionStatus = "success";
        state.actionInstanceId = null;
      })
      .addCase(stopEc2.rejected, (state, action) => {
        state.actionStatus = "error";
        state.actionInstanceId = null;
        state.errorMessage = (action.payload as any)?.message || "Failed to stop the instance";
      })
      .addCase(fetchEc2AuditLogs.pending, (state) => {
        state.auditLogsFetchStatus = "loading";
        state.auditLogs = [];
      })
      .addCase(fetchEc2AuditLogs.fulfilled, (state, action) => {
        state.auditLogsFetchStatus = action.payload?.code === SUCCESS_CODE ? "success" : "error";
        state.auditLogs = action.payload?.data ?? [];
      })
      .addCase(fetchEc2AuditLogs.rejected, (state) => {
        state.auditLogsFetchStatus = "error";
        state.auditLogs = [];
      });
  },
});

export const { resetEc2State } = ec2Slice.actions;

export const useEc2Slice = () => useAppSelector((state) => state.ec2Slice);

export default ec2Slice.reducer;
